import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { User, GameMode } from '../types';
import { useAppStore } from '../stores/appStore';
import { useShallow } from 'zustand/react/shallow';
import { useLogout, useInbox, useFriendRequests } from '../hooks/useGame';
import { useQuery } from '@tanstack/react-query';
import { fetchCollabInbox } from '../services/api';
import {
    skipToNextTrack,
    skipToPreviousTrack,
    getTrackProgress,
    togglePlayPause,
} from '../services/audio';
import { fetchBlitzState, fetchSystemStatus } from '../services/api';
import LoginModal from './LoginModal';
import ProfileModal from './ProfileModal';
import InboxModal from './InboxModal';
import FriendsModal from './FriendsModal';
import HowItWorksModal from './HowItWorksModal';
import PuzzleListModal from './PuzzleListModal';
import AchievementsModal from './AchievementsModal';
import MusicPlayerModal from './MusicPlayerModal';
import StoreModal from './StoreModal';
import BackpackModal from './BackpackModal';
import './Navbar.css';
import './ProfileModal.css'; // For badge styles
import './NavbarUserMenu.css';

interface NavbarProps {
    user: User | null;
    puzzleLabel?: string;
    isComplete?: boolean;
}

const MODE_CONFIG: Record<GameMode, { label: string; icon: string; title: string; description: string; badge: string; image: string }> = {
    classic: {
        label: 'Classic',
        icon: '🌡️',
        title: 'Hot Or Cold? Classic',
        description: 'Find the secret word using semantic similarity.',
        badge: 'Play',
        image: '/static/thermometer.png'
    },
    flip: {
        label: 'Flip',
        icon: '🎴',
        title: 'Hot Or Cold? Flip',
        description: 'Clear the board by guessing related words.',
        badge: 'Play',
        image: '/static/arrows.png'
    },
    blitz: {
        label: 'Blitz',
        icon: '🔥',
        title: 'Hot Or Cold? Blitz',
        description: 'Race against others to find the word first!',
        badge: 'Live',
        image: '/static/flame.png'
    },
    bridge: {
        label: 'Bridge',
        icon: '🌉',
        title: 'Hot Or Cold? Bridge',
        description: 'Connect two words through semantic steps!',
        badge: 'New',
        image: '/static/bridge.png'
    },
    swipe: {
        label: 'Swipe',
        icon: '↔️',
        title: 'Hot Or Cold? Swipe',
        description: 'Swipe left or right to sort words by meaning!',
        badge: 'Beta',
        image: '/static/rotate.png'
    },
    rotations: {
        label: 'Rotations',
        icon: '🔄',
        title: 'Hot Or Cold? Rotations',
        description: 'Rotate the rings to spell six words!',
        badge: 'Beta',
        image: '/static/rotate.png'
    },
};

export function Navbar({ user, puzzleLabel, isComplete }: NavbarProps) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isInboxModalOpen, setIsInboxModalOpen] = useState(false);
    const [isFriendsModalOpen, setIsFriendsModalOpen] = useState(false);
    const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
    const [isPuzzleListModalOpen, setIsPuzzleListModalOpen] = useState(false);
    const [isAchievementsModalOpen, setIsAchievementsModalOpen] = useState(false);
    const [isMusicPlayerModalOpen, setIsMusicPlayerModalOpen] = useState(false);
    const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
    const [isBackpackModalOpen, setIsBackpackModalOpen] = useState(false);
    const [isRewardTooltipOpen, setIsRewardTooltipOpen] = useState(false);
    const [trackProgress, setTrackProgress] = useState({ current: 0, duration: 0 });
    const [isBlitzPaused, setIsBlitzPaused] = useState(false);
    // Read music state from store — always in sync, no polling needed
    const nowPlayingTrack = useAppStore(s => s.nowPlayingTrack);
    const musicIsActive = useAppStore(s => s.musicIsActive);
    const musicIsPaused = useAppStore(s => s.musicIsPaused);
    const [isSwipeHidden, setIsSwipeHidden] = useState(false);

    useEffect(() => {
        fetchBlitzState()
            .then(state => {
                if (state.paused || state.phase === 'stopped') {
                    setIsBlitzPaused(true);
                }
            })
            .catch(console.error);

        // Fetch system status to check if Swipe is hidden
        fetchSystemStatus()
            .then(status => {
                if (status.swipe_hidden) {
                    setIsSwipeHidden(true);
                }
            })
            .catch(console.error);
    }, []);

    // Poll progress only when the menu is open and music is active
    useEffect(() => {
        if (!isMenuOpen || !musicIsActive) return;
        const tick = () => setTrackProgress(getTrackProgress());
        tick();
        const id = setInterval(tick, 500);
        return () => clearInterval(id);
    }, [isMenuOpen, musicIsActive, nowPlayingTrack]);

    const { mutate: logout } = useLogout();
    const { data: inboxData } = useInbox();
    const { data: friendRequests } = useFriendRequests();
    const { data: collabData } = useQuery({
        queryKey: ['collabInbox'],
        queryFn: fetchCollabInbox,
    });

    // React to global login modal trigger from game components
    const loginModalOpen = useAppStore(state => state.loginModalOpen);
    const setLoginModalOpen = useAppStore(state => state.setLoginModalOpen);
    useEffect(() => {
        if (loginModalOpen) {
            setIsLoginModalOpen(true);
            setLoginModalOpen(false);
        }
    }, [loginModalOpen, setLoginModalOpen]);

    // Consolidated store access using useShallow to prevent unnecessary re-renders
    const {
        currentMode,
        storeCoins,
        musicVolume,
        sfxVolume,
        setMusicVolume,
        setSfxVolume,
        isDarkMode,
        toggleDarkMode,
        puzzles,
        currentPuzzleId,
        setCurrentPuzzleId,
        setGameState,
        isCollab,
    } = useAppStore(
        useShallow(state => ({
            currentMode: state.currentMode,
            storeCoins: state.user?.coins,
            musicVolume: state.musicVolume,
            sfxVolume: state.sfxVolume,
            setMusicVolume: state.setMusicVolume,
            setSfxVolume: state.setSfxVolume,
            isDarkMode: state.isDarkMode,
            toggleDarkMode: state.toggleDarkMode,
            puzzles: state.puzzles,
            currentPuzzleId: state.currentPuzzleId,
            setCurrentPuzzleId: state.setCurrentPuzzleId,
            setGameState: state.setGameState,
            isCollab: state.isCollab,
        }))
    );

    // ... Puzzle navigation logic ...
    const currentIndex = puzzles.findIndex(p => p.id === currentPuzzleId);
    const currentPuzzle = currentIndex >= 0 ? puzzles[currentIndex] : null;

    const hasMultiplePuzzles = puzzles.length > 1;

    // If currentPuzzleId doesn't exist in puzzles list, auto-reset to last puzzle
    // This handles cases where an old/deleted puzzle ID is persisted
    if (puzzles.length > 0 && currentPuzzleId && currentIndex === -1) {
        const lastPuzzle = puzzles[puzzles.length - 1];
        setGameState(null);
        setCurrentPuzzleId(lastPuzzle.id);
    }

    const handlePrevPuzzle = () => {
        if (hasMultiplePuzzles) {
            let newIndex = currentIndex - 1;
            if (newIndex < 0) newIndex = puzzles.length - 1;
            const prevPuzzle = puzzles[newIndex];
            setGameState(null);
            setCurrentPuzzleId(prevPuzzle.id);
        }
    };

    const handleNextPuzzle = () => {
        if (hasMultiplePuzzles) {
            let newIndex = currentIndex + 1;
            if (newIndex >= puzzles.length) newIndex = 0;
            const nextPuzzle = puzzles[newIndex];
            setGameState(null);
            setCurrentPuzzleId(nextPuzzle.id);
        }
    };

    const handleModeChange = (mode: GameMode) => {
        // Navigate to the new mode URL to trigger proper puzzle list fetching
        window.location.href = `/play?mode=${mode}`;
    };

    const handleLogout = () => {
        logout(undefined, {
            onSuccess: () => {
                setIsUserMenuOpen(false);
            }
        });
    };

    // Calculate badges
    // const unreadCount = user?.unread_messages || 0; // Use live data
    const unreadCount = inboxData?.messages?.filter((m: any) => !(m.read || m.is_read)).length || 0;
    // const friendRequestCount = user?.friend_requests_count || 0;
    const friendRequestCount = friendRequests?.length || 0;
    const partyInviteCount = collabData?.requests?.length || 0;
    const userAchievements = user?.achievements || [];
    const newAchievementCount = userAchievements.filter(a => a.just_unlocked).length;

    console.log("[Navbar] Badge counts:", { unreadCount, friendRequestCount, newAchievementCount, inboxData, friendRequests });
    const isToday = currentPuzzle?.display_date === new Date().toISOString().split('T')[0];

    // Listen for custom events to open login modal from other components
    useEffect(() => {
        const handleOpenLoginModal = () => setIsLoginModalOpen(true);
        window.addEventListener('openLoginModal', handleOpenLoginModal);
        return () => window.removeEventListener('openLoginModal', handleOpenLoginModal);
    }, []);

    return (
        <>
            <nav className={`navbar mode-${currentMode}`}>
                <div className="navbar-left">
                    <button
                        className={`navbar-menu-toggle ${isMenuOpen ? 'active' : ''}`}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                </div>

                <div className="navbar-center">
                    {currentMode === 'blitz' ? (
                        /* Blitz mode: Show generic title to keep puzzle anonymous */
                        <div className="puzzle-selector">
                            <div className="puzzle-selector-details">
                                <span className="puzzle-selector-title">
                                    🔥 Blitz Mode
                                </span>
                                <span className="puzzle-selector-meta">
                                    <span className="puzzle-today-badge">
                                        ⚡ Live
                                    </span>
                                </span>
                            </div>
                        </div>
                    ) : (
                        /* Other modes: Show normal puzzle selector */
                        <div className="puzzle-selector">
                            <button
                                className={`puzzle-nav-btn ${!hasMultiplePuzzles ? 'disabled' : ''}`}
                                onClick={handlePrevPuzzle}
                                disabled={!hasMultiplePuzzles}
                                aria-label="Previous puzzle"
                            >
                                ‹
                            </button>
                            <div className="puzzle-selector-details">
                                <span className="puzzle-selector-title">
                                    {currentPuzzle?.linked_achievement && (
                                        <span className="puzzle-reward-wrapper">
                                            <button
                                                className="puzzle-reward-badge"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsRewardTooltipOpen(!isRewardTooltipOpen);
                                                }}
                                                title="Special Reward! Click for details"
                                            >
                                                🎁
                                            </button>
                                            <AnimatePresence>
                                                {isRewardTooltipOpen && (
                                                    <>
                                                        <motion.div
                                                            className="puzzle-reward-backdrop"
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            onClick={() => setIsRewardTooltipOpen(false)}
                                                        />
                                                        <motion.div
                                                            className="puzzle-reward-tooltip"
                                                            initial={{ opacity: 0, scale: 0.9, y: -5 }}
                                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                                            exit={{ opacity: 0, scale: 0.9, y: -5 }}
                                                            transition={{ type: 'spring', duration: 0.3 }}
                                                        >
                                                            <div className="reward-tooltip-header">
                                                                <span className="reward-tooltip-icon">🏆</span>
                                                                <span className="reward-tooltip-title">{currentPuzzle.linked_achievement.name}</span>
                                                            </div>
                                                            <p className="reward-tooltip-desc">
                                                                {currentPuzzle.linked_achievement.description}
                                                            </p>
                                                            {currentPuzzle.linked_achievement.reward_avatar && (
                                                                <div className="reward-tooltip-reward">
                                                                    <span className="reward-label">🎨 Reward:</span>
                                                                    <span className="reward-value">Exclusive Avatar</span>
                                                                </div>
                                                            )}
                                                            {currentPuzzle.linked_achievement.reward_song && (
                                                                <div className="reward-tooltip-reward">
                                                                    <span className="reward-label">🎵 Reward:</span>
                                                                    <span className="reward-value">Special Track</span>
                                                                </div>
                                                            )}
                                                            <p className="reward-tooltip-hint">
                                                                Complete this puzzle to unlock!
                                                            </p>
                                                        </motion.div>
                                                    </>
                                                )}
                                            </AnimatePresence>
                                        </span>
                                    )}
                                    {currentPuzzle?.label || puzzleLabel || 'Loading...'}
                                    {isComplete && (
                                        <img
                                            src="/static/check.png"
                                            alt="Completed"
                                            className="puzzle-complete-check"
                                        />
                                    )}
                                    {isCollab && (
                                        <span className="collab-badge" title="Collaborative Session">
                                            🤝
                                        </span>
                                    )}
                                </span>
                                <span className="puzzle-selector-meta">
                                    {isToday ? (
                                        <span className="puzzle-today-badge">
                                            🚩 Today!
                                        </span>
                                    ) : (
                                        <span className="puzzle-date-text">
                                            {currentPuzzle?.display_date || ''}
                                        </span>
                                    )}
                                </span>
                                {/* Threshold Club Ribbon */}
                                {(currentPuzzle?.min_completion_threshold ?? 0) > 0 && (
                                    <span className="puzzle-threshold-ribbon">
                                        🏆 {currentPuzzle?.min_completion_threshold}% Club
                                    </span>
                                )}
                            </div>
                            <button
                                className={`puzzle-nav-btn ${!hasMultiplePuzzles ? 'disabled' : ''}`}
                                onClick={handleNextPuzzle}
                                disabled={!hasMultiplePuzzles}
                                aria-label="Next puzzle"
                            >
                                ›
                            </button>
                        </div>
                    )}
                </div>

                <div className="navbar-right">
                    {user?.authenticated ? (
                        (() => {
                            // Check if user is in top 3 for current mode
                            const modeKey = currentMode === 'blitz' ? 'classic' : currentMode;
                            const rank = user.global_ranks?.[modeKey as keyof typeof user.global_ranks];
                            const crownClass = rank === 1 ? 'crown-gold' : rank === 2 ? 'crown-silver' : rank === 3 ? 'crown-bronze' : '';
                            const hasNotifications = unreadCount > 0 || friendRequestCount > 0 || partyInviteCount > 0;

                            return (
                                <button
                                    className={`navbar-user-button ${crownClass}`}
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    aria-label="User menu"
                                >
                                    {/* Crown indicator for top 3 */}
                                    {rank && rank <= 3 && (
                                        <span className="navbar-crown">👑</span>
                                    )}

                                    {/* Notification badge */}
                                    {hasNotifications && (
                                        <span className="navbar-notification-badge">
                                            {unreadCount + friendRequestCount + partyInviteCount}
                                        </span>
                                    )}

                                    {/* Fallback initial - always rendered, sits behind image */}
                                    <span className="navbar-user-initial">
                                        {user.username?.charAt(0).toUpperCase()}
                                    </span>

                                    {/* Image - absolute positioned on top. Hides on error. */}
                                    {user.avatar && (
                                        <img
                                            src={`/static/avatars/${user.avatar}`}
                                            alt={user.username}
                                            className="navbar-user-avatar"
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    )}
                                </button>
                            );
                        })()
                    ) : (
                        <button
                            className="navbar-user-button guest"
                            onClick={() => setIsLoginModalOpen(true)}
                            aria-label="Log in"
                        >
                            <span className="navbar-user-initial" style={{ color: 'var(--text-secondary)' }}>
                                ?
                            </span>
                        </button>
                    )}
                </div>

                {/* Burger Menu */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <>
                            <motion.div
                                className="navbar-menu-backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setIsMenuOpen(false)}
                            />
                            <motion.div
                                className="navbar-menu"
                                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                                transition={{ type: 'spring', duration: 0.4, bounce: 0.3 }}
                            >
                                <div className="navbar-menu-section">
                                    <p className="menu-eyebrow">Game Modes</p>
                                    <div className="menu-options">
                                        {(Object.keys(MODE_CONFIG) as GameMode[])
                                            .filter(mode => (mode !== 'blitz' || !isBlitzPaused) && (mode !== 'swipe' || !isSwipeHidden))
                                            .map((mode) => (
                                                <button
                                                    key={mode}
                                                    className={`menu-option ${mode === currentMode ? 'active' : ''}`}
                                                    onClick={() => handleModeChange(mode)}
                                                    data-mode={mode}
                                                >
                                                    <span className="menu-option-icon-wrap">
                                                        <img src={MODE_CONFIG[mode].image} alt={`${MODE_CONFIG[mode].title} icon`} />
                                                    </span>
                                                    <div className="menu-option-text">
                                                        <span className="menu-option-title">{MODE_CONFIG[mode].title}</span>
                                                        <span className="menu-option-desc">{MODE_CONFIG[mode].description}</span>
                                                    </div>
                                                    <span className={`menu-option-badge ${MODE_CONFIG[mode].badge.toLowerCase()}`}>
                                                        {MODE_CONFIG[mode].badge}
                                                    </span>
                                                </button>
                                            ))}
                                        {currentMode !== 'blitz' && (
                                            <button className="menu-option" data-mode="list" onClick={() => {
                                                setIsMenuOpen(false);
                                                setIsPuzzleListModalOpen(true);
                                            }}>
                                                <span className="menu-option-icon-wrap">
                                                    <img src="/static/to-do-list.png" alt="Puzzle List" />
                                                </span>
                                                <div className="menu-option-text">
                                                    <span className="menu-option-title">Puzzle List</span>
                                                    <span className="menu-option-desc">Browse past games.</span>
                                                </div>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="navbar-menu-section navbar-quick-actions">
                                    <p className="menu-eyebrow">Quick Actions</p>
                                    <div className="menu-options">
                                        <button className="menu-option small" data-mode="help" onClick={() => {
                                            setIsMenuOpen(false);
                                            setIsHowItWorksOpen(true);
                                        }}>
                                            <span className="menu-option-icon-wrap">
                                                <img src="/static/question-sign.png" alt="Help icon" />
                                            </span>
                                            <div className="menu-option-text">
                                                <span className="menu-option-title">How to Play</span>
                                            </div>
                                        </button>
                                        <div className="menu-volume-sliders">
                                            <div className="volume-slider-row">
                                                <span className="volume-label">🎵</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={musicVolume * 100}
                                                    onChange={(e) => setMusicVolume(Number(e.target.value) / 100)}
                                                    className="volume-slider"
                                                />
                                                <span className="volume-value">{Math.round(musicVolume * 100)}%</span>
                                            </div>
                                            <div className="volume-slider-row">
                                                <span className="volume-label">🔊</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={sfxVolume * 100}
                                                    onChange={(e) => setSfxVolume(Number(e.target.value) / 100)}
                                                    className="volume-slider"
                                                />
                                                <span className="volume-value">{Math.round(sfxVolume * 100)}%</span>
                                            </div>
                                            {/* Mini Audio Player */}
                                            {musicIsActive && nowPlayingTrack && (
                                                <div className="mini-player">
                                                    <div className="mini-player-info">
                                                        <button
                                                            className={`mini-player-icon-btn ${musicIsPaused ? '' : 'playing'}`}
                                                            onClick={togglePlayPause}
                                                            aria-label={musicIsPaused ? "Play" : "Pause"}
                                                        >
                                                            {musicIsPaused ? '▶️' : '⏸️'}
                                                        </button>
                                                        <div className="mini-player-track-info">
                                                            <span className="mini-player-track">{nowPlayingTrack}</span>
                                                            <div
                                                                className="mini-player-progress"
                                                                style={{
                                                                    '--progress': `${trackProgress.duration ? (trackProgress.current / trackProgress.duration) * 100 : 0}%`
                                                                } as React.CSSProperties}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mini-player-controls">
                                                        <button
                                                            className="mini-player-btn"
                                                            onClick={skipToPreviousTrack}
                                                            aria-label="Previous track"
                                                        >
                                                            ⏮️
                                                        </button>
                                                        <button
                                                            className="mini-player-btn"
                                                            onClick={skipToNextTrack}
                                                            aria-label="Next track"
                                                        >
                                                            ⏭️
                                                        </button>
                                                        <button
                                                            className="mini-player-btn settings"
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                                setIsMusicPlayerModalOpen(true);
                                                            }}
                                                            aria-label="Music settings"
                                                        >
                                                            ⚙️
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <button className="menu-option small" data-mode="theme" onClick={toggleDarkMode}>
                                            <span className="menu-option-icon-wrap">
                                                <img src={isDarkMode ? "/static/sun.png" : "/static/moon.png"} alt="Theme icon" />
                                            </span>
                                            <div className="menu-option-text">
                                                <span className="menu-option-title">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                                            </div>
                                        </button>
                                    </div>
                                    <div className="menu-footer-icons">
                                        <a
                                            href="https://www.reddit.com/r/HotOrColdGame"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="menu-icon-link"
                                            aria-label="Reddit Community"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            <img src="/static/reddit.png" alt="Reddit" />
                                        </a>
                                        <a
                                            href="mailto:admin@hotorcold.io"
                                            className="menu-icon-link"
                                            aria-label="Contact Support"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            <img src="/static/email.png" alt="Email" />
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* User Menu */}
                <AnimatePresence>
                    {isUserMenuOpen && user?.authenticated && (
                        <>
                            <motion.div
                                className="navbar-menu-backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setIsUserMenuOpen(false)}
                            />
                            <motion.div
                                className="navbar-user-menu-container"
                                initial={{ opacity: 0, scale: 0.92, y: -8 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.92, y: -8 }}
                                transition={{ type: "spring", duration: 0.4, bounce: 0.3 }}
                            >
                                {/* Header */}
                                <div className="user-menu-header">
                                    <div className="user-menu-avatar-wrapper">
                                        <div className="user-menu-avatar-fallback-large">
                                            {user.username?.charAt(0).toUpperCase()}
                                        </div>
                                        {user.avatar && (
                                            <img
                                                src={`/static/avatars/${user.avatar}`}
                                                alt={user.username}
                                                className="user-menu-avatar-large"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="user-menu-info">
                                        <h4 className="user-menu-username">{user.username}</h4>
                                        <div className="user-menu-status">
                                            <span className="user-status-dot"></span>
                                            Online
                                        </div>
                                        {/* Global ranking badges */}
                                        {(user.global_ranks?.classic && user.global_ranks.classic <= 3) ||
                                            (user.global_ranks?.flip && user.global_ranks.flip <= 3) ||
                                            (user.global_ranks?.bridge && user.global_ranks.bridge <= 3) ? (
                                            <div className="user-menu-crowns">
                                                {user.global_ranks?.classic && user.global_ranks.classic <= 3 && (
                                                    <span className={`crown-badge rank-${user.global_ranks.classic}`}>
                                                        👑 Classic #{user.global_ranks.classic}
                                                    </span>
                                                )}
                                                {user.global_ranks?.flip && user.global_ranks.flip <= 3 && (
                                                    <span className={`crown-badge rank-${user.global_ranks.flip}`}>
                                                        👑 Flip #{user.global_ranks.flip}
                                                    </span>
                                                )}
                                                {user.global_ranks?.bridge && user.global_ranks.bridge <= 3 && (
                                                    <span className={`crown-badge rank-${user.global_ranks.bridge}`}>
                                                        👑 Bridge #{user.global_ranks.bridge}
                                                    </span>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="user-menu-balance">
                                        <span className="balance-value">{storeCoins ?? user.coins ?? 0}</span>
                                        <span className="balance-icon">🪙</span>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="user-menu-stats">
                                    <p className="stats-header">Streak • All Puzzles</p>
                                    <div className="menu-stat-item">
                                        <div className="stat-left">
                                            <img src={MODE_CONFIG.classic.image} alt="Classic" className="stat-icon-img" />
                                            <span className="stat-label">Classic</span>
                                        </div>
                                        <div className="stat-values">
                                            <span className="stat-value" title="Current streak">🔥 {user.streaks?.classic || 0}</span>
                                            <span className="stat-separator">•</span>
                                            <span className="stat-percent" title="Completion">
                                                {user.completions?.classic?.total
                                                    ? Math.round((user.completions.classic.completed / user.completions.classic.total) * 100)
                                                    : 0}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="menu-stat-item">
                                        <div className="stat-left">
                                            <img src={MODE_CONFIG.flip.image} alt="Flip" className="stat-icon-img" />
                                            <span className="stat-label">Flip</span>
                                        </div>
                                        <div className="stat-values">
                                            <span className="stat-value" title="Current streak">🔥 {user.streaks?.flip || 0}</span>
                                            <span className="stat-separator">•</span>
                                            <span className="stat-percent" title="Completion">
                                                {user.completions?.flip?.total
                                                    ? Math.round((user.completions.flip.completed / user.completions.flip.total) * 100)
                                                    : 0}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="menu-stat-item">
                                        <div className="stat-left">
                                            <img src={MODE_CONFIG.bridge.image} alt="Bridge" className="stat-icon-img" />
                                            <span className="stat-label">Bridge</span>
                                        </div>
                                        <div className="stat-values">
                                            <span className="stat-value" title="Current streak">🔥 {user.streaks?.bridge || 0}</span>
                                            <span className="stat-separator">•</span>
                                            <span className="stat-percent" title="Completion">
                                                {user.completions?.bridge?.total
                                                    ? Math.round((user.completions.bridge.completed / user.completions.bridge.total) * 100)
                                                    : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Menu Grid */}
                                <div className="user-menu-grid">
                                    <button className="user-menu-button" data-action="profile" onClick={() => {
                                        setIsUserMenuOpen(false);
                                        setIsProfileModalOpen(true);
                                    }}>
                                        <span className="menu-btn-icon">👤</span>
                                        <div className="menu-btn-content">
                                            <span className="menu-btn-title">Profile</span>
                                            <span className="menu-btn-desc">Avatar & settings</span>
                                        </div>
                                    </button>

                                    <button className="user-menu-button" data-action="inbox" onClick={() => {
                                        setIsUserMenuOpen(false);
                                        setIsInboxModalOpen(true);
                                    }}>
                                        <span className="menu-btn-icon menu-icon-with-badge">
                                            📥
                                            {unreadCount > 0 && <span className="menu-icon-badge">{unreadCount}</span>}
                                        </span>
                                        <div className="menu-btn-content">
                                            <span className="menu-btn-title">Inbox</span>
                                            <span className="menu-btn-desc">{unreadCount === 0 && partyInviteCount === 0 ? "All clear" : `${unreadCount + partyInviteCount} new`}</span>
                                        </div>
                                        {(unreadCount + partyInviteCount) > 0 && <span className="menu-grid-badge">{unreadCount + partyInviteCount}</span>}
                                    </button>

                                    <button className="user-menu-button" data-action="friends" onClick={() => {
                                        setIsUserMenuOpen(false);
                                        setIsFriendsModalOpen(true);
                                    }}>
                                        <span className="menu-btn-icon">👥</span>
                                        <div className="menu-btn-content">
                                            <span className="menu-btn-title">Friends</span>
                                            <span className="menu-btn-desc">{friendRequestCount > 0 ? `${friendRequestCount} pending` : "Manage friends"}</span>
                                        </div>
                                        {friendRequestCount > 0 && <span className="menu-grid-badge">{friendRequestCount}</span>}
                                    </button>

                                    <button className="user-menu-button" data-action="achievements" onClick={() => {
                                        setIsUserMenuOpen(false);
                                        setIsAchievementsModalOpen(true);
                                    }}>
                                        <span className="menu-btn-icon">🏆</span>
                                        <div className="menu-btn-content">
                                            <span className="menu-btn-title">Trophies</span>
                                            <span className="menu-btn-desc">{userAchievements.filter(a => a.unlocked).length}/{userAchievements.length} unlocked</span>
                                        </div>
                                        {newAchievementCount > 0 && <span className="menu-grid-badge">{newAchievementCount}</span>}
                                    </button>

                                    <button className="user-menu-button" data-action="store" onClick={() => {
                                        setIsUserMenuOpen(false);
                                        setIsStoreModalOpen(true);
                                    }}>
                                        <span className="menu-btn-icon">🏪</span>
                                        <div className="menu-btn-content">
                                            <span className="menu-btn-title">Store</span>
                                            <span className="menu-btn-desc">Buy items</span>
                                        </div>
                                    </button>

                                    <button className="user-menu-button" data-action="backpack" onClick={() => {
                                        setIsUserMenuOpen(false);
                                        setIsBackpackModalOpen(true);
                                    }}>
                                        <span className="menu-btn-icon">🎒</span>
                                        <div className="menu-btn-content">
                                            <span className="menu-btn-title">Backpack</span>
                                            <span className="menu-btn-desc">Your items</span>
                                        </div>
                                    </button>
                                </div>

                                {/* Footer */}
                                <div className="user-menu-footer">
                                    <button className="logout-btn" onClick={handleLogout}>
                                        <span>↪️</span> Log Out
                                    </button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </nav>

            <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
            />

            <ProfileModal
                isOpen={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
            />

            <InboxModal
                isOpen={isInboxModalOpen}
                onClose={() => setIsInboxModalOpen(false)}
            />

            <FriendsModal
                isOpen={isFriendsModalOpen}
                onClose={() => setIsFriendsModalOpen(false)}
            />

            <HowItWorksModal
                isOpen={isHowItWorksOpen}
                onClose={() => setIsHowItWorksOpen(false)}
                initialMode={currentMode}
            />

            <PuzzleListModal
                isOpen={isPuzzleListModalOpen}
                onClose={() => setIsPuzzleListModalOpen(false)}
            />

            <AchievementsModal
                isOpen={isAchievementsModalOpen}
                onClose={() => setIsAchievementsModalOpen(false)}
                achievements={userAchievements}
            />

            <MusicPlayerModal
                isOpen={isMusicPlayerModalOpen}
                onClose={() => setIsMusicPlayerModalOpen(false)}
            />

            <StoreModal
                isOpen={isStoreModalOpen}
                onClose={() => setIsStoreModalOpen(false)}
            />

            <BackpackModal
                isOpen={isBackpackModalOpen}
                onClose={() => setIsBackpackModalOpen(false)}
                onOpenStore={() => {
                    setIsBackpackModalOpen(false);
                    setIsStoreModalOpen(true);
                }}
            />
        </>
    );
}

export default Navbar;

